'use client';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import MenuDropdown from './MenuDropdown';
import { useWidth } from '@/lib/hooks/useWidth';
import ContentfulLink from '../ContentfulLink/ContentfulLink';
import Link from 'next/link';
import { NavigationItem, NavigationLink } from '@/types/contentful';
import { NavigationData } from './types';
import { HeaderEyebrow } from '../HeaderEyebrow/HeaderEyebrow';
import { usePathname } from 'next/navigation';
import { addTrailingSlashToInternalUrl } from '@/lib/utils/trailingSlash';

export default function MenuForDesktop({
  data,
  primaryCta,
  callRailCta,
  isReducedBrandSite = false
}: {
  data: NavigationData | null;
  primaryCta: NavigationLink | null;
  callRailCta: NavigationLink | null;
  isReducedBrandSite: boolean
}) {
  const SCROLL_HIDE_THRESHOLD = 80;
  const SCROLL_SHOW_THRESHOLD = 10;
  const EYEBROW_TRANSITION_MS = 400;
  const SCROLL_DELTA_THRESHOLD = 4;

  const [activeMenu, setActiveMenu] = useState('');
  const [isEyebrowVisible, setIsEyebrowVisible] = useState(true);
  const pathname = usePathname();
  const hideHeaderEyebrow = (pathname?.startsWith('/ready') || isReducedBrandSite) ?? false;

  const logoContainerRef = useRef<HTMLDivElement>(null);

  const lastScrollYRef = useRef(0);
  const isTransitioningRef = useRef(false);

  const { width } = useWidth(logoContainerRef, activeMenu);

  const navItems = data?.items || [];

  const closeActiveMenu = () => {
    setActiveMenu('');
  };

  // Scroll handler to show/hide the eyebrow based on scroll direction and position.
  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      if (isTransitioningRef.current) return;

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;

      if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) return;

      if (currentScrollY <= SCROLL_SHOW_THRESHOLD) {
        if (!isEyebrowVisible) {
          setIsEyebrowVisible(true);
        }
        lastScrollYRef.current = currentScrollY;
        return;
      }

      if (currentScrollY > SCROLL_HIDE_THRESHOLD) {
        if (delta > 0 && isEyebrowVisible) {
          setIsEyebrowVisible(false);
        } else if (delta < 0 && !isEyebrowVisible) {
          setIsEyebrowVisible(true);
        }
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isEyebrowVisible]);

  // When the eyebrow visibility changes, set a flag to prevent scroll handling during the transition,
  // and reset it after the transition duration.
  useEffect(() => {
    isTransitioningRef.current = true;

    const timer = window.setTimeout(() => {
      isTransitioningRef.current = false;
      lastScrollYRef.current = window.scrollY;
    }, EYEBROW_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [isEyebrowVisible]);

  const handleClick = (id: string) => {
    setActiveMenu(activeMenu === id ? '' : id);
  };

  // The nav bar is rendered twice: once inside the fixed overlay (interactive) and once inside the
  // in-flow placeholder below (inert). `isPlaceholder` strips the interactivity/refs from the copy.
  const renderNavBar = (isPlaceholder: boolean) => (
    <nav className='relative flex justify-between items-center py-4 border-b border-border-global bg-white'>
      <div ref={isPlaceholder ? undefined : logoContainerRef}>
        <Link
          href='/'
          tabIndex={isPlaceholder ? -1 : undefined}
          className='pl-12 pr-12 2xl:pr-24 block'
          aria-label='Go to homepage'
          onClick={closeActiveMenu}
        >
          {data?.logo?.src && (
            <Image
              src={data.logo.src}
              alt={data.logo.alt || data.logo.title || 'Logo'}
              width={data.logo.width || 140}
              height={data.logo.height || 48}
              className='brand-logo-desktop'
              loading='eager'
              fetchPriority='high'
            />
          )}
        </Link>
      </div>
      <div className='flex items-center gap-8'>
        {navItems.map((item: NavigationItem) => {
          if (item.__typename === 'NavigationMenu') {
            return (
              <MenuDropdown
                key={item.id}
                items={item.items}
                isOpen={isPlaceholder ? false : activeMenu === item.id}
                onClose={closeActiveMenu}
                toggleLabel={item.menuName || item.title}
                handleClick={isPlaceholder ? () => {} : () => handleClick(item.id)}
                leftOffset={width}
              />
            );
          }
          if (item.__typename === 'Link') {
            return (
              <div
                key={`${item.id}-${item.label}`}
                className='relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-secondary-500 after:transition-transform after:duration-300 hover:after:scale-x-100 after:scale-x-0'
              >
                <Link
                  href={addTrailingSlashToInternalUrl(item.url) || '#'}
                  tabIndex={isPlaceholder ? -1 : undefined}
                  className='heading-6 font-semibold flex items-center py-4 gap-4 cursor-pointer text-primary-500'
                  onClick={closeActiveMenu}
                >
                  {item.label}
                </Link>
              </div>
            );
          }
          return null;
        })}
      </div>

      <div className='ml-auto pr-12 flex items-center gap-4'>
        {callRailCta ? (
          <ContentfulLink
            data={{
              ...callRailCta,
              context: 'desktop-navigation',
            }}
          />
        ) : null}
        {/* {primaryCta ? <ContentfulLink data={primaryCta} /> : null} */}
        {primaryCta ? (
          <ContentfulLink
            data={{
              ...primaryCta,
              context: 'desktop-navigation',
            }}
          />
        ) : null}
      </div>
    </nav>
  );

  return (
    <>
      <div className='fixed top-0 left-0 z-20 hidden w-full xl:block'>
        {!hideHeaderEyebrow && (
          <div
            className={`overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.23,1,0.32,1)] ${
              isEyebrowVisible ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <HeaderEyebrow
              couponOffer={data?.couponOffer ?? null}
              brandCode={data?.brandCode ?? ''}
            />
          </div>
        )}
        {renderNavBar(false)}
      </div>

      <div className='invisible hidden w-full xl:block'>
        {!hideHeaderEyebrow && <div className='h-10' />}
        {renderNavBar(true)}
      </div>
    </>
  );
}
